import { Controller } from "@hotwired/stimulus";
import Sortable from "sortablejs";

// Composable-blocks editor for public-site pages. Architecture:
//
//   • The DOM is the source of truth. Each block-card is a
//     `[data-page-editor-target="block"]` with `data-block-id` and
//     `data-block-type`. Inside it, every input that should be persisted
//     carries `data-page-editor-target="setting"` and a dotted
//     `data-setting-path` (e.g. "headline" or "cards.0.title").
//
//   • Auto-save → DRAFT (PublicPage#draft_blocks). Never touches the
//     published `blocks`. The iframe loads the public page with a
//     signed `?preview=...` so the admin sees their draft. Visitors
//     keep seeing the published version.
//
//   • Explicit "Publish" promotes draft → live (PublicPage#blocks).
//     Cmd/Ctrl+S triggers Publish too. Disabled when there's nothing
//     unpublished to publish.
//
//   • Leaving with edits that haven't reached the server (mid-debounce
//     or mid-request) asks first — `beforeunload` for hard loads AND
//     `turbo:before-visit` for Turbo Drive navigations (which never
//     fire beforeunload). Drafts left "unpublished" are intentional —
//     leaving the editor doesn't lose them.
export default class extends Controller {
  static targets = ["field", "list", "block", "setting", "summary", "chevron",
                    "body", "preview", "addMenu", "addPanel", "template",
                    "status", "publishButton", "publishLabel", "discardButton",
                    "pageSettings", "pageSetting", "pageSettingsField", "templateSelect"];
  static values = {
    saveUrl:           String,
    publishUrl:        String,
    previewUrl:        String,
    themeUrl:          String,
    currentTemplate:   String,
    csrf:              String,
    initialPublished:  String,
    // Param scope the auto-save posts under — "public_page" for the
    // website builder, "funnel_step" for funnel steps.
    paramScope:        { type: String, default: "public_page" },
  };

  saveTimer = null;
  saving = false;
  publishing = false;
  // A theme switch is a DRAFT (server sets pending_template); this flag
  // lets Publish fire on a theme-only change even when the blocks didn't
  // move, and shows the unpublished-draft status.
  themeDraft = false;

  connect() {
    // Snapshot of the LIVE published state at page load. Used to drive
    // the "has draft" / "no draft" status without an extra round-trip.
    // Both sides go through canonicalJson() — the server stores blocks
    // in jsonb (which re-orders object keys) and Rails' to_json escapes
    // HTML entities, so raw string equality against the DOM
    // serialization would phantom-diff on nearly every page.
    this.publishedJson = this.publishedSnapshot();
    this.lastSavedJson = this.editorSnapshot();
    this.refreshState();

    // Forms inside the editor shell (Redesign with Oscar, Layout ideas,
    // Discard) navigate via Turbo — flush any un-debounced edit first
    // or it's silently lost (and a redesign would build on stale
    // blocks).
    this.element.addEventListener("submit", this.onFormSubmit, true);

    // Drag-and-drop reordering via SortableJS. Restricted to the
    // explicit drag handle so clicking anywhere else in a card doesn't
    // accidentally pick it up. On drop, we re-serialize and queue an
    // auto-save (DOM order IS the truth).
    this.sortable = Sortable.create(this.listTarget, {
      handle:        "[data-page-editor-drag-handle]",
      draggable:     "[data-page-editor-target='block']",
      animation:     150,
      ghostClass:    "page-editor-ghost",
      chosenClass:   "page-editor-chosen",
      forceFallback: true,
      fallbackClass: "page-editor-dragging",
      onEnd: (evt) => {
        if (evt.oldIndex === evt.newIndex) return;
        this.markDraftDirty();
        this.scheduleSave();
      },
    });

    // Auto-save on any input/change inside the editor.
    this.element.addEventListener("input",  this.onAnyInput);
    this.element.addEventListener("change", this.onAnyInput);

    // Cmd/Ctrl+S → publish.
    this.onKeydown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "s") {
        e.preventDefault();
        this.publishNow();
      }
    };
    document.addEventListener("keydown", this.onKeydown);

    // Warn before leaving only if edits haven't landed on the server
    // yet — mid-debounce (`unsavedDraft`) or mid-request (`saving`).
    // Unpublished drafts are explicit user state and don't need a
    // warning — they persist server-side. Two listeners because the
    // admin app navigates two ways:
    //   • beforeunload  — hard loads (close tab, reload, external URL)
    //   • turbo:before-visit — Turbo Drive navigations (sidebar links),
    //     which never fire beforeunload, so without this a stray click
    //     silently discarded in-flight edits.
    this.hasUnsavedEdits = () => this.unsavedDraft || this.saving;
    this.onBeforeUnload = (e) => {
      if (!this.hasUnsavedEdits()) return;
      e.preventDefault();
      e.returnValue = "Your latest edits haven't been saved as a draft yet.";
      return e.returnValue;
    };
    window.addEventListener("beforeunload", this.onBeforeUnload);

    this.onBeforeVisit = (e) => {
      if (!this.hasUnsavedEdits()) return;
      if (!confirm("Your latest edits haven't been saved as a draft yet. Leave anyway?")) {
        e.preventDefault();
      }
    };
    document.addEventListener("turbo:before-visit", this.onBeforeVisit);

    // Click outside the add-menu closes it.
    document.addEventListener("click", this.onDocumentClick);
  }

  disconnect() {
    this.element.removeEventListener("submit", this.onFormSubmit, true);
    document.removeEventListener("click", this.onDocumentClick);
    document.removeEventListener("keydown", this.onKeydown);
    window.removeEventListener("beforeunload", this.onBeforeUnload);
    document.removeEventListener("turbo:before-visit", this.onBeforeVisit);
    clearTimeout(this.saveTimer);
    this.sortable?.destroy();
  }

  // A form submission inside the editor (Oscar redesign, Layout ideas,
  // Discard drafts) is about to leave/replace the page. If an edit is
  // still sitting in the save debounce (or a save is mid-flight), hold
  // the submission, flush the draft, then re-submit — otherwise the
  // pending edit is dropped and whatever the form triggers reads stale
  // draft blocks.
  onFormSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.dataset.pageEditorFlushed) { delete form.dataset.pageEditorFlushed; return; }
    if (!this.unsavedDraft && !this.saving) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    this.flushThenResubmit(form);
  };

  async flushThenResubmit(form) {
    clearTimeout(this.saveTimer);
    while (this.saving) await new Promise((r) => setTimeout(r, 100));
    if (this.unsavedDraft) await this.save();
    // Best effort — even if the save failed, let the user's action
    // proceed rather than dead-clicking the button.
    form.dataset.pageEditorFlushed = "true";
    form.requestSubmit();
  }

  onAnyInput = (e) => {
    // Two flavors of inputs flow through here. Block-level settings
    // (`setting`) get the per-card summary refresh; page-level settings
    // (`pageSetting`) just queue a save.
    const blockInput = e.target.closest("[data-page-editor-target='setting']");
    if (blockInput) { this.onSettingChange(blockInput); return; }
    const pageInput  = e.target.closest("[data-page-editor-target='pageSetting']");
    if (pageInput) {
      // The nav's top-of-page state is only visible at scroll 0, but
      // the iframe reload restores scroll position — pop the preview
      // back to the top so the change actually shows.
      if (pageInput.dataset.settingPath === "nav_default_style") this.pendingPreviewScrollTop = true;
      this.markDraftDirty();
      this.scheduleSave();
    }
  };

  // ── Card-level actions ──────────────────────────────────────────

  toggle(event) {
    // Works for both block cards and the pinned Page settings card —
    // either kind has a `body`/`chevron` target inside its wrapper.
    const card = event.currentTarget.closest(
      "[data-page-editor-target='block'], [data-page-editor-target='pageSettings']"
    );
    if (!card) return;
    const body = card.querySelector("[data-page-editor-target='body']");
    const chev = card.querySelector("[data-page-editor-target='chevron']");
    const open = !body.hasAttribute("hidden");
    if (open) { body.setAttribute("hidden", ""); chev?.classList.remove("rotate-180"); }
    else      { body.removeAttribute("hidden");   chev?.classList.add("rotate-180"); }
  }

  // Collapse/expand every block card at once — long pages (especially
  // primitive-heavy funnel steps) get hard to scan otherwise.
  collapseAll() { this.blockTargets.forEach((card) => this.setCardOpen(card, false)); }
  expandAll()   { this.blockTargets.forEach((card) => this.setCardOpen(card, true)); }

  setCardOpen(card, open) {
    const body = card.querySelector("[data-page-editor-target='body']");
    const chev = card.querySelector("[data-page-editor-target='chevron']");
    if (!body) return;
    if (open) { body.removeAttribute("hidden");   chev?.classList.add("rotate-180"); }
    else      { body.setAttribute("hidden", "");  chev?.classList.remove("rotate-180"); }
  }

  remove(event) {
    if (!confirm("Remove this block?")) return;
    const card = event.currentTarget.closest("[data-page-editor-target='block']");
    card.remove();
    this.markDraftDirty();
    this.scheduleSave();
  }

  // ── Add menu ────────────────────────────────────────────────────

  toggleAdd(event) {
    event.stopPropagation();
    const menu = this.addMenuTarget;
    if (menu.hasAttribute("hidden")) menu.removeAttribute("hidden");
    else menu.setAttribute("hidden", "");
  }

  addBlock(event) {
    const type = event.currentTarget.dataset.blockType;
    const tpl = this.templateTargets.find(t => t.dataset.templateType === type);
    if (!tpl) return;

    const fragment = tpl.content.cloneNode(true);
    const card = fragment.firstElementChild;
    card.dataset.blockId = this.uuid();

    this.listTarget.appendChild(card);
    this.addMenuTarget.setAttribute("hidden", "");

    // Visual feedback: scroll new card into view + brief highlight.
    card.scrollIntoView({ behavior: "smooth", block: "center" });
    card.style.transition = "background-color 800ms ease";
    card.style.backgroundColor = "rgba(59,130,246,0.08)";
    setTimeout(() => { card.style.backgroundColor = ""; }, 900);

    this.markDraftDirty();
    this.scheduleSave();
  }

  onDocumentClick = (event) => {
    if (!this.hasAddMenuTarget) return;
    if (this.addPanelTarget.contains(event.target)) return;
    this.addMenuTarget.setAttribute("hidden", "");
  };

  // ── Settings input handler ──────────────────────────────────────

  onSettingChange(input) {
    // Live-update the collapsed-card summary for headline/heading
    // paths so the stack stays scannable while editing.
    const card = input.closest("[data-page-editor-target='block']");
    const summary = card?.querySelector("[data-page-editor-target='summary']");
    if (summary && [ "headline", "heading" ].includes(input.dataset.settingPath)) {
      summary.textContent = (input.value || "").slice(0, 80);
    }
    this.markDraftDirty();
    this.scheduleSave();
  }

  reloadPreview() {
    if (!this.hasPreviewTarget) return;
    if (this.pendingPreviewScrollTop) {
      this.pendingPreviewScrollTop = false;
      const onLoad = () => {
        this.previewTarget.removeEventListener("load", onLoad);
        try { this.previewTarget.contentWindow.scrollTo(0, 0); } catch (_) {}
      };
      this.previewTarget.addEventListener("load", onLoad);
    }
    try {
      this.previewTarget.contentWindow.location.reload();
    } catch (_) {
      this.previewTarget.src = this.previewTarget.src;
    }
  }

  // Theme switcher — repoints the preview iframe at the same page with a
  // `?template=<key>` override AND persists the pick as a draft
  // (pending_template) so Publish actually keeps it. Without the persist,
  // switching a theme then publishing reverted to the live skin (the
  // `?template=` override is preview-only).
  previewTemplate(event) {
    const key = event.target.value;
    if (this.hasPreviewTarget && this.previewUrlValue) {
      const url = new URL(this.previewUrlValue, window.location.origin);
      if (key) url.searchParams.set("template", key);
      else url.searchParams.delete("template");
      this.previewTarget.src = url.toString();
    }
    this.persistThemeDraft(key);
  }

  async persistThemeDraft(key) {
    if (!this.hasThemeUrlValue) return;
    // Selecting the already-live theme is not a draft.
    this.themeDraft = key !== this.currentTemplateValue;
    this.refreshState();
    try {
      const fd = new FormData();
      fd.append("authenticity_token", this.csrfValue);
      fd.append("template", key);
      fd.append("_method", "patch");
      await fetch(this.themeUrlValue, {
        method: "POST",
        body: fd,
        headers: { "Accept": "application/json" },
        credentials: "same-origin",
      });
    } catch (e) {
      // Non-fatal: the preview already reflects the pick; publish will
      // re-drive from the server's pending_template if it persisted.
    }
  }

  // ── Serialization ───────────────────────────────────────────────

  serialize() {
    return Array.from(this.listTarget.querySelectorAll("[data-page-editor-target='block']")).map(card => {
      const settings = {};
      card.querySelectorAll("[data-page-editor-target='setting']").forEach(input => {
        const path = input.dataset.settingPath;
        if (!path) return;
        const type = input.dataset.settingType || "string";
        let value;
        if (type === "boolean") {
          value = input.checked;
        } else if (type === "array_of_strings") {
          value = (input.value || "").split("\n").map(s => s.trim()).filter(Boolean);
        } else if (type === "number") {
          value = input.value === "" ? null : Number(input.value);
        } else {
          value = input.value;
        }
        this.setByPath(settings, path, value);
      });
      return {
        id: card.dataset.blockId,
        type: card.dataset.blockType,
        settings: settings,
      };
    });
  }

  setByPath(obj, path, value) {
    const parts = path.split(".");
    let cur = obj;
    for (let i = 0; i < parts.length - 1; i++) {
      const key = parts[i];
      const next = parts[i + 1];
      const isArrayContainer = /^\d+$/.test(next);
      if (cur[key] === undefined || cur[key] === null) {
        cur[key] = isArrayContainer ? [] : {};
      }
      cur = cur[key];
    }
    cur[parts[parts.length - 1]] = value;
  }

  // Walks every `[data-page-editor-target='pageSetting']` input in the
  // pinned page-settings card and builds a nested object via dotted
  // paths (e.g. "hero.headline" -> { hero: { headline: ... } }).
  serializePageSettings() {
    const out = {};
    if (!this.hasPageSettingsTarget) return out;
    this.pageSettingsTarget.querySelectorAll("[data-page-editor-target='pageSetting']").forEach(input => {
      const path = input.dataset.settingPath;
      if (!path) return;
      const type = input.dataset.settingType || "string";
      let value;
      if (type === "boolean") {
        value = input.checked;
      } else if (type === "array_of_strings") {
        value = (input.value || "").split("\n").map(s => s.trim()).filter(Boolean);
      } else if (type === "number") {
        value = input.value === "" ? null : Number(input.value);
      } else {
        value = input.value;
      }
      this.setByPath(out, path, value);
    });
    return out;
  }

  // ── Auto-save (DRAFT) ──────────────────────────────────────────

  markDraftDirty() {
    this.unsavedDraft = true;
    this.setStatus("editing");
  }

  scheduleSave() {
    clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => this.save(), 800);
  }

  async save() {
    if (this.saving) {
      this.scheduleSave();
      return;
    }

    const blocks           = this.serialize();
    const pageSettings     = this.serializePageSettings();
    const blocksJson       = JSON.stringify(blocks);
    const pageSettingsJson = JSON.stringify(pageSettings);
    const combined         = this.canonicalJson(blocks) + "::" + this.canonicalJson(pageSettings);

    if (combined === this.lastSavedJson) {
      this.unsavedDraft = false;
      this.refreshState();
      return;
    }

    this.saving = true;
    this.setStatus("savingDraft");

    try {
      // Inside the try so a missing target can't strand `saving`
      // (flushAndPublish spins on it).
      if (this.hasFieldTarget) this.fieldTarget.value = blocksJson;
      if (this.hasPageSettingsFieldTarget) this.pageSettingsFieldTarget.value = pageSettingsJson;

      const fd = new FormData();
      fd.append(`${this.paramScopeValue}[blocks_json]`,        blocksJson);
      fd.append(`${this.paramScopeValue}[page_settings_json]`, pageSettingsJson);
      fd.append("authenticity_token", this.csrfValue);
      const res = await fetch(this.saveUrlValue, {
        method: "PATCH",
        body: fd,
        headers: { "Accept": "application/json" },
        credentials: "same-origin",
      });
      if (res.ok) {
        this.lastSavedJson = combined;
        this.unsavedDraft = false;
        this.reloadPreview();
        this.refreshState();
      } else {
        let detail = "";
        try {
          const body = await res.json();
          detail = (body.errors || []).join(", ");
        } catch (_) {}
        this.setStatus("error", `Save failed (${res.status})${detail ? " · " + detail : ""}`);
      }
    } catch (e) {
      this.setStatus("error", "Save failed (network)");
    } finally {
      this.saving = false;
    }
  }

  // ── Publish (DRAFT → LIVE) ─────────────────────────────────────

  async publishNow(event) {
    if (event) event.preventDefault();
    await this.publish();
  }

  publish() {
    return this.flushAndPublish();
  }

  // First flush any in-flight save (so the draft on the server matches
  // what's in the editor), then POST publish.
  async flushAndPublish() {
    if (this.publishing) return;

    // An auto-save may be mid-flight — wait it out, or save() would
    // just re-schedule itself and this publish would silently no-op
    // (the "publish button doesn't work right after editing" bug).
    while (this.saving) await new Promise((r) => setTimeout(r, 100));

    const live = this.editorSnapshot();
    if (this.unsavedDraft || live !== this.lastSavedJson) {
      clearTimeout(this.saveTimer);
      await this.save();
      if (this.unsavedDraft) return;
    }

    if (!this.hasPublishedDiff() && !this.themeDraft) {
      // Nothing to publish — surface a brief confirmation.
      this.setStatus("noOp");
      setTimeout(() => this.refreshState(), 1200);
      return;
    }

    this.publishing = true;
    this.setStatus("publishing");

    try {
      const fd = new FormData();
      fd.append("authenticity_token", this.csrfValue);
      const res = await fetch(this.publishUrlValue, {
        method: "POST",
        body: fd,
        headers: { "Accept": "application/json" },
        credentials: "same-origin",
      });
      if (res.ok) {
        this.publishedJson = this.lastSavedJson;
        // The theme draft is now live — clear it and re-baseline so a
        // fresh switch is detectable again.
        this.themeDraft = false;
        if (this.hasTemplateSelectTarget) this.currentTemplateValue = this.templateSelectTarget.value;
        this.setStatus("published");
        // Reload the preview so the iframe reflects the now-live state.
        this.reloadPreview();
      } else {
        this.setStatus("error", `Publish failed (${res.status})`);
      }
    } catch (e) {
      this.setStatus("error", "Publish failed (network)");
    } finally {
      this.publishing = false;
    }
  }

  hasPublishedDiff() {
    return this.lastSavedJson !== this.publishedJson;
  }

  // Recalculate the toolbar pill + Publish button state.
  refreshState() {
    if (this.unsavedDraft) {
      this.setStatus("editing");
    } else if (this.hasPublishedDiff() || this.themeDraft) {
      this.setStatus("draftSaved");
    } else {
      this.setStatus("live");
    }
  }

  // ── Status pill + button choreography ──────────────────────────

  setStatus(state, customText) {
    const cfg = {
      live:        { text: "Live",                cls: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",  icon: "✓" },
      editing:     { text: "Unsaved changes",     cls: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",  icon: "●" },
      savingDraft: { text: "Saving draft…",       cls: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",      icon: "↻" },
      draftSaved:  { text: "Unpublished draft",   cls: "bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200",  icon: "●" },
      publishing:  { text: "Publishing…",         cls: "bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300",      icon: "↻" },
      published:   { text: "Published just now",  cls: "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300",  icon: "✓" },
      noOp:        { text: "Already published",   cls: "bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300",         icon: "✓" },
      error:       { text: customText || "Save failed", cls: "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300",    icon: "!" },
    }[state];

    if (cfg && this.hasStatusTarget) {
      this.statusTarget.className =
        "inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full " + cfg.cls;
      this.statusTarget.innerHTML = `<span aria-hidden="true">${cfg.icon}</span><span>${cfg.text}</span>`;
    }

    // Publish button reflects whether there's a draft worth publishing.
    if (this.hasPublishButtonTarget) {
      // Unsaved edits count: flushAndPublish saves first, so the button
      // must stay clickable during the debounce/save window instead of
      // dead-clicking right after a change.
      const canPublish = (this.hasPublishedDiff() || this.unsavedDraft || this.saving || this.themeDraft) && !this.publishing;
      this.publishButtonTarget.disabled = !canPublish;
      this.publishButtonTarget.classList.toggle("opacity-50", !canPublish);
      this.publishButtonTarget.classList.toggle("cursor-not-allowed", !canPublish);

      const label = this.hasPublishLabelTarget ? this.publishLabelTarget : null;
      if (label) {
        if (state === "publishing") label.textContent = "Publishing…";
        else if (state === "published" || state === "live") label.textContent = "Published";
        else if (canPublish) label.textContent = "Publish changes";
        else label.textContent = "Published";
      }
    }

    // Discard button only relevant when a draft is parked unpublished.
    if (this.hasDiscardButtonTarget) {
      this.discardButtonTarget.classList.toggle("hidden", !this.hasPublishedDiff());
    }
  }

  valueOrEmpty(name) {
    const cap = name.charAt(0).toUpperCase() + name.slice(1);
    return this[`has${cap}Value`] ? this[`${name}Value`] : "{}";
  }

  // ── Snapshot canonicalization ──────────────────────────────────
  //
  // Draft/published diffing compares canonical JSON, never raw strings:
  // the server stores blocks in jsonb (PostgreSQL re-orders object
  // keys), Rails' to_json unicode-escapes HTML entities, and a card
  // partial may render an input (serialized as "") for a key the stored
  // settings simply omit. Keys are sorted and empty values (null, "",
  // [], {}) dropped on BOTH sides so none of that reads as a diff.

  editorSnapshot() {
    return this.canonicalJson(this.serialize()) + "::" + this.canonicalJson(this.serializePageSettings());
  }

  publishedSnapshot() {
    try {
      const snap = JSON.parse(this.valueOrEmpty("initialPublished"));
      return this.canonicalJson(snap.blocks || []) + "::" + this.canonicalJson(snap.page_settings || {});
    } catch (_) {
      return this.canonicalJson([]) + "::" + this.canonicalJson({});
    }
  }

  canonicalJson(value) {
    return JSON.stringify(this.canonicalize(value));
  }

  canonicalize(value) {
    if (Array.isArray(value)) return value.map((v) => this.canonicalize(v));
    if (value && typeof value === "object") {
      const out = {};
      for (const key of Object.keys(value).sort()) {
        const v = this.canonicalize(value[key]);
        if (v === null || v === "") continue;
        if (Array.isArray(v) && v.length === 0) continue;
        if (typeof v === "object" && !Array.isArray(v) && Object.keys(v).length === 0) continue;
        out[key] = v;
      }
      return out;
    }
    return value;
  }

  uuid() {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
    return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, c => {
      const r = (Math.random() * 16) | 0;
      const v = c === "x" ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }
}
